import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import { useGetEventBhaRunsQuery } from "@api/pApi";
import { BhaSummary } from "./BhaSummary";
import { fmtDepth, fmtNum } from "@lib/format";
import type { AssemblyOut, BhaRunOut, BitOpOut } from "@api/types";
import type { MessageKey } from "@i18n/messages";
import type { Locale } from "@i18n/locales";

/** The translate fn from useLocale, passed down to the leaf render helpers. */
type TFn = (key: MessageKey) => string;

/**
 * BHA & Bit screen — the event's bottom-hole-assembly runs, each with the
 * assembly it pulled (BHA metadata) and its bit operations. Every value is
 * served by Oxbo-P (§10.3 /events/{id}/bha-runs); the FE only lays it out and
 * never recomputes a metric.
 */
export function Bha() {
  const { t } = useLocale();
  const { well, activeEvent, isLoading, isError } = useCurrentWell();

  return (
    <div>
      <div className="page-head">
        <h1>{t("bha.title")}</h1>
        <p className="subtitle">
          {well ? `${well.commonName} · ${well.field}` : t("well.notSelected")}
        </p>
      </div>
      <QueryState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!activeEvent}
        emptyMessage={t("well.event.noEvents")}
      >
        {well?.uwi ? <BhaSummary wellUwi={well.uwi} /> : null}
        {activeEvent ? <BhaPanel eventId={activeEvent.id} /> : null}
      </QueryState>
    </div>
  );
}

/** The BHA runs panel for a single event. Split out so the screen wrapper only
 *  resolves the active event. */
function BhaPanel({ eventId }: { eventId: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetEventBhaRunsQuery(eventId, {
    skip: !eventId,
  });

  // Guard: `runs` and `bit_ops` are always arrays in the contract, but be
  // defensive against a malformed payload so the screen never throws.
  const runs = Array.isArray(data?.runs) ? data!.runs : [];
  const bitOpCount = runs.reduce(
    (n, r) => n + (Array.isArray(r.bit_ops) ? r.bit_ops.length : 0),
    0,
  );

  const stats: Stat[] = data
    ? [
        { label: t("bha.kpi.runs"), value: fmtNum(runs.length, locale) },
        { label: t("bha.kpi.bitOps"), value: fmtNum(bitOpCount, locale), tone: "ops" },
      ]
    : [];

  return (
    <QueryState
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && runs.length === 0}
      emptyMessage={t("bha.noData")}
      onRetry={refetch}
    >
      <div className="page-head" style={{ marginBottom: 12 }}>
        <p className="subtitle">{t("bha.subtitle")}</p>
      </div>
      {stats.length ? <StatBand stats={stats} /> : null}

      <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
        {runs.map((run, i) => (
          <RunCard key={run.id} run={run} index={i} locale={locale} t={t} />
        ))}
      </div>
    </QueryState>
  );
}

/** A single BHA run: its dates/MD span, the linked assembly (BHA metadata), and
 *  its bit-operations table. */
function RunCard({
  run,
  index,
  locale,
  t,
}: {
  run: BhaRunOut;
  index: number;
  locale: Locale;
  t: TFn;
}) {
  const bitOps = Array.isArray(run.bit_ops) ? run.bit_ops : [];
  const title = run.assembly?.assembly_name ?? `${t("bha.run")} ${index + 1}`;

  return (
    <div className="card" data-owner="p">
      <div className="panel-head-row">
        <h3>{title}</h3>
        <span className="mono" style={{ fontSize: 12, color: "var(--dim)" }}>
          {fmtDate(run.date_in, locale)} → {fmtDate(run.date_out, locale)}
        </span>
      </div>

      <div className="kv-grid">
        <KV k={t("bha.run.dates")} v={`${fmtDate(run.date_in, locale)} / ${fmtDate(run.date_out, locale)}`} />
        <KV
          k={t("bha.run.mdSpan")}
          v={`${fmtDepth(run.md_in, locale)} / ${fmtDepth(run.md_out, locale)}`}
          mono
        />
        {run.assembly ? (
          <AssemblyFields assembly={run.assembly} locale={locale} t={t} />
        ) : null}
      </div>

      {!run.assembly ? (
        <div className="empty-state" style={{ marginTop: 8 }}>
          {t("bha.run.noAssembly")}
        </div>
      ) : null}

      <h4 style={{ marginTop: 14 }}>{t("bha.bitOps")}</h4>
      {bitOps.length === 0 ? (
        <div className="empty-state">{t("bha.bitOps.empty")}</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("bha.col.date")}</th>
                <th className="num">{t("bha.col.md")}</th>
                <th className="num">{t("bha.col.duration")}</th>
                <th className="num">{t("bha.col.progress")}</th>
                <th className="num">{t("bha.col.rop")}</th>
                <th className="num">{t("bha.col.wob")}</th>
                <th className="num">{t("bha.col.rpm")}</th>
                <th className="num">{t("bha.col.flow")}</th>
                <th className="num">{t("bha.col.standpipe")}</th>
                <th>{t("bha.col.code")}</th>
              </tr>
            </thead>
            <tbody>
              {bitOps.map((op) => (
                <BitOpRow key={op.id} op={op} locale={locale} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** The assembly's BHA metadata as key/value pairs. Components are not carried on
 *  the payload — the assembly IS the BHA descriptor. */
function AssemblyFields({
  assembly,
  locale,
  t,
}: {
  assembly: AssemblyOut;
  locale: Locale;
  t: TFn;
}) {
  // Hole size is quoted in its own UOM (inches by default) — not a depth.
  const holeSize =
    assembly.hole_size != null
      ? `${fmtNum(assembly.hole_size, locale, { maximumFractionDigits: 3 })}${
          assembly.hole_size_uom ? ` ${assembly.hole_size_uom}` : ""
        }`
      : "—";
  return (
    <>
      <KV k={t("bha.assembly.no")} v={assembly.assembly_no != null ? String(assembly.assembly_no) : "—"} />
      <KV k={t("bha.assembly.stringType")} v={assembly.string_type_entry_key ?? "—"} mono />
      <KV k={t("bha.assembly.phase")} v={assembly.phase_entry_key ?? "—"} mono />
      <KV k={t("bha.assembly.holeSize")} v={holeSize} mono />
      <KV k={t("bha.assembly.mdTop")} v={fmtDepth(assembly.md_top, locale)} mono />
      <KV k={t("bha.assembly.mdBase")} v={fmtDepth(assembly.md_base, locale)} mono />
      <KV k={t("bha.assembly.length")} v={fmtDepth(assembly.length_total, locale)} mono />
    </>
  );
}

function BitOpRow({ op, locale }: { op: BitOpOut; locale: Locale }) {
  return (
    <tr data-owner="p">
      <td className="mono" style={{ whiteSpace: "nowrap" }}>{fmtDate(op.date_op, locale)}</td>
      <td className="num tab-nums">{fmtDepth(op.md_op, locale)}</td>
      <td className="num tab-nums">{fmtNum(op.op_duration, locale, { maximumFractionDigits: 1 })}</td>
      <td className="num tab-nums">{fmtDepth(op.op_progress, locale)}</td>
      <td className="num tab-nums">{fmtNum(op.rop, locale, { maximumFractionDigits: 1 })}</td>
      <td className="num tab-nums">{fmtRange(op.wob_min, op.wob_max, locale)}</td>
      <td className="num tab-nums">{fmtRange(op.rpm_min, op.rpm_max, locale)}</td>
      <td className="num tab-nums">{fmtNum(op.flowrate, locale, { maximumFractionDigits: 0 })}</td>
      <td className="num tab-nums">{fmtNum(op.pressure_standpipe, locale, { maximumFractionDigits: 0 })}</td>
      <td>
        <span className="mono">{op.activity_code_entry_key ?? "—"}</span>
      </td>
    </tr>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="kv">
      <span className="kv-k">{k}</span>
      <span className={`kv-v${mono ? " mono" : ""}`}>{v}</span>
    </div>
  );
}

/** A server date string (ISO date or datetime) in the tenant locale; "—" when null. */
function fmtDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

/** "min–max", collapsing to a single value when one bound is missing. */
function fmtRange(min: number | null, max: number | null, locale: Locale): string {
  const lo = fmtNum(min, locale, { maximumFractionDigits: 0 });
  const hi = fmtNum(max, locale, { maximumFractionDigits: 0 });
  if (min == null && max == null) return "—";
  if (min == null) return hi;
  if (max == null) return lo;
  return `${lo}–${hi}`;
}
